import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { applyRoutineEdit, type RoutineEdit } from './routine-edit';
import { useRoutinesDuplexer } from './use-routines-duplexer';

// The Routines screen's data layer. The READ goes to `/api/routines`, which proxies the server's
// `routines` native-data action — the ONE computed payload web, iOS and Android share (see that route
// for why `/api/pulses` is not it). The EDIT write goes to `/api/routine-update`, which proxies the
// `update-routine` action both phones already use: it validates the cron server-side and answers
// `400 invalid_cron`, so a schedule the server will not take is refused at the field rather than stored
// and left to silently stop firing. The pause toggle and the delete stay on `/api/pulses/{id}`, which is
// what those two operations have always used and what the entity accepts directly.

/** Why a routine will not run. Mirrors the server's `RoutinePauseReason`. */
export type RoutinePauseReason =
  | 'OUT_OF_CREDITS'
  | 'CONNECTOR_DISCONNECTED'
  | 'PAUSED_BY_USER'
  | 'ONBOARDING_INCOMPLETE'
  | 'NO_AGENTS'
  | null;

/**
 * A routine as the enriched endpoint returns it: the PulseIdentity entity plus the run-state it does
 * not carry. Declared here rather than imported from common-lib so nothing in this module's type graph
 * reaches the ESM-only chain that breaks jest in CI.
 */
export interface WebRoutine {
  id: string;
  revision?: string;
  name?: string;
  description?: string;
  heartbeat?: string;
  cron?: string;
  workspaceId?: string;
  paused?: boolean;
  pausedOutOfCredits?: boolean;
  /** Server-set: a genome recalc paused this because its connector looked disconnected. */
  pausedConnectorDisconnected?: boolean;
  triggers?: unknown[];
  createdDate?: string;
  display?: Record<string, unknown>;
  /** The connector whose kit shipped this routine (`gmail`). Absent on a user- or agent-made routine —
   *  it is what lets the row credit the connected app it came from (see lib/skill-connector). */
  sourceConnectorSlug?: string;
  /** The genome template that connector's kit built this routine from. Absent on a user- or agent-made
   *  routine. With `paused` and `activated` it decides which tab the routine is on (`routine-tabs`). */
  sourceTemplateSlug?: string;
  /** Set by the BFF, one-way: the routine has been live at least once — pulse-identity leaves a trace on
   *  every resume and on every system pause, and the BFF reads it. Absent on a server older than the field. */
  activated?: boolean;
  /** The sub-group this routine is filed under (`briefing`, `chasing`), from the genome's
   *  `params/routine-groups.json`. An ID, never a heading: the words come from this app's own locale
   *  catalog (`routines.group.<id>`), so a routine the agent shapes lands under the same heading as one
   *  from a kit. Absent on a routine created before the field existed — `routineGroup` reads that as
   *  `general`. */
  groupKey?: string;

  /** Computed: fires on its own and is live. */
  scheduled?: boolean;
  /** Computed: next cron fire time in the site timezone (ISO), or null. */
  nextRun?: string | null;
  /** Computed: has at least one event trigger. */
  hasTriggers?: boolean;
  /** Computed: when the routine's job is over (ISO), null when open-ended. See lib/routine-end. */
  endsAt?: string | null;
  /** Computed: the routine has ended — "Ended <date>", no next run, no switch. */
  ended?: boolean;

  /** Run history. `runsKnown` gates the other three — see `routineActivity`. */
  lastRun?: string | null;
  lastRunTaskCount?: number;
  runCount?: number;
  runCountCapped?: boolean;
  runsKnown?: boolean;

  /** The agent that last did work for this routine (a routine has no owner). The server sends `null`
   *  for "nobody has"; `fetchRoutines` normalizes that to absent so the shared `FilterableRoutine`
   *  shape (and every `?.` on it) reads the same as every other optional id in the app. */
  agentId?: string;
  agentName?: string | null;

  pauseReason?: RoutinePauseReason;
  pauseNote?: string | null;
}

export type RoutinesState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; routines: WebRoutine[] };

async function fetchRoutines(workspaceId?: string): Promise<WebRoutine[]> {
  const res = await apiFetch(`${API_BASE}/api/routines`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(workspaceId ? { routineWorkspaceIds: [workspaceId] } : {}),
  });
  if (!res.ok) throw new Error(`routines: ${res.status}`);
  const data = (await res.json()) as { routines?: (WebRoutine & { agentId?: string | null })[] };
  return (data.routines ?? [])
    .filter((routine) => !!routine?.id)
    .map((routine) => ({ ...routine, agentId: routine.agentId ?? undefined }));
}

/** Warm the account-wide routines list into the React Query cache on app mount, so the Your Agents
 *  summary tile doesn't sit on a spinner. Best-effort; swallows errors. */
export function prefetchRoutines(): void {
  void queryClient
    .prefetchQuery({ queryKey: queryKeys.routines(undefined), queryFn: () => fetchRoutines(undefined) })
    .catch(() => {});
}

/**
 * The routines in scope. `workspaceId` scopes the fetch SERVER-side (a workspace's Dashboard card);
 * the standalone screen fetches account-wide and narrows in the client, so the Space picker can widen
 * back out — the same split `useTasks`/`TasksBody` settled on.
 */
export function useRoutines(workspaceId?: string): { state: RoutinesState; refresh: () => Promise<void> } {
  const query = useQuery({
    queryKey: queryKeys.routines(workspaceId),
    queryFn: () => fetchRoutines(workspaceId),
  });

  // A routine changed anywhere — this screen, a connector's card in the chat, another device, or the
  // routine pausing ITSELF. It hangs off the query rather than off a screen so every reader gets it:
  // the Routines screen, a Space's Dashboard card, and each connector card in the transcript, which are
  // exactly the surfaces that can be open at once and disagree.
  useRoutinesDuplexer(() => void query.refetch());
  // A refetch error while we already hold a good list stays 'ready' — only surface an error before the
  // first success, matching `usePulses`.
  const state: RoutinesState = query.data
    ? { kind: 'ready', routines: query.data }
    : query.status === 'error'
      ? { kind: 'error' }
      : { kind: 'loading' };
  const refresh = () =>
    queryClient.refetchQueries({ queryKey: queryKeys.routinesAllScopes }).then(() => undefined);
  return { state, refresh };
}

/** Map a routine across every cached scope — a routine can sit in the account-wide list and a
 *  workspace-scoped one at the same time, and an optimistic edit has to reach both. */
function eachList(fn: (list: WebRoutine[]) => WebRoutine[]): void {
  queryClient.setQueriesData<WebRoutine[]>({ queryKey: queryKeys.routinesAllScopes }, (list) =>
    list ? fn(list) : list,
  );
}

function findRoutine(id: string): WebRoutine | null {
  for (const [, list] of queryClient.getQueriesData<WebRoutine[]>({ queryKey: queryKeys.routinesAllScopes })) {
    const found = list?.find((routine) => routine.id === id);
    if (found) return found;
  }
  return null;
}

function replaceInCache(id: string, replacement: WebRoutine): void {
  eachList((list) => list.map((routine) => (routine.id === id ? replacement : routine)));
}

export type RoutineUpdates = {
  paused?: boolean;
  name?: string;
  description?: string;
  cron?: string;
};

export type WriteResult = { ok: boolean };

/**
 * Optimistic patch: apply locally, PATCH the server, roll back on failure.
 *
 * The server echoes the raw PulseIdentity, which carries NONE of the computed fields — so the echo is
 * merged OVER the cached routine rather than replacing it. Replacing would blank `nextRun`, `agentName`
 * and the whole run history the moment a user flipped a switch, which is precisely the class of bug the
 * enriched read exists to prevent.
 *
 * Both merges go through `applyRoutineEdit`, so a cron change drops the fields the server derived from
 * the OLD cron rather than letting them survive the merge.
 */
export async function patchRoutine(routine: WebRoutine, updates: RoutineUpdates): Promise<WriteResult> {
  const id = routine.id;
  if (!id) return { ok: false };
  const previous = findRoutine(id);
  if (previous) replaceInCache(id, applyRoutineEdit(previous, updates));
  try {
    const res = await apiFetch(`${API_BASE}/api/pulses/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      if (previous) replaceInCache(id, previous);
      return { ok: false };
    }
    const data = (await res.json()) as { pulse?: Partial<WebRoutine> };
    const current = findRoutine(id);
    if (current && data.pulse) {
      replaceInCache(id, { ...applyRoutineEdit({ ...current, ...data.pulse }, updates), id });
    }
    return { ok: true };
  } catch {
    if (previous) replaceInCache(id, previous);
    return { ok: false };
  }
}

/** Why an edit was refused. `invalid_cron` is the one the FORM handles, on the schedule field. */
export type RoutineEditResult = { ok: true } | { ok: false; reason: 'invalid_cron' | 'failed' };

/**
 * Save an edit through the `update-routine` action — the write path both phones use.
 *
 * The cache is patched AFTER the server accepts (not optimistically, as the toggle is), because this
 * write can be REFUSED on its content: an `invalid_cron` leaves the sheet open with the typed text
 * intact, and a card that had already re-rendered under a schedule the server rejected would be lying
 * while the user corrects it.
 */
export async function updateRoutine(routine: WebRoutine, edit: RoutineEdit): Promise<RoutineEditResult> {
  const id = routine.id;
  if (!id) return { ok: false, reason: 'failed' };
  try {
    const res = await apiFetch(`${API_BASE}/api/routine-update`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ routineId: id, ...edit }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      if (text.includes('invalid_cron')) {
        return { ok: false, reason: 'invalid_cron' };
      }
      return { ok: false, reason: 'failed' };
    }
    const current = findRoutine(id);
    if (current) replaceInCache(id, applyRoutineEdit(current, edit));
    return { ok: true };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

export async function deleteRoutine(id: string): Promise<WriteResult> {
  const previous = findRoutine(id);
  eachList((list) => list.filter((routine) => routine.id !== id));
  try {
    const res = await apiFetch(`${API_BASE}/api/pulses/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) {
      if (previous) restore(previous);
      return { ok: false };
    }
    return { ok: true };
  } catch {
    if (previous) restore(previous);
    return { ok: false };
  }
}

function restore(routine: WebRoutine): void {
  eachList((list) => (list.some((r) => r.id === routine.id) ? list : [routine, ...list]));
}
